import * as React from 'react';
import { Paper, Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PANEL_RADIUS, SHADOWS, SP, hoverOnly } from '../../theme/tokens';
import useContainerWidth from './useContainerWidth';

/**
 * Wie viele Spuren der Mindestbreite passen in `breite`?
 *
 * `Math.floor((breite + abstand) / (mindestbreite + abstand))` — die
 * Abstandsglieder sind der Grund, warum die Form nicht einfach
 * `floor(breite / mindestbreite)` ist: bei `n` Spuren liegen `n − 1` Abstaende
 * zwischen ihnen, und wer den Abstand nicht mitzaehlt, zaehlt bei jeder Breite
 * eine Spur zu viel.
 *
 * 🔴 **Der Abstand dieses Rasters ist 0, und das ist gemessen, nicht gesetzt:**
 *    die Trennung zwischen zwei Kacheln ist die `borderLeft` der Zelle, und die
 *    liegt INNERHALB ihrer Spur (`box-sizing: border-box`). Das Raster traegt
 *    kein `gap`. Die Glieder bleiben trotzdem stehen, weil sie die Form
 *    vollstaendig machen — eine spaetere `gap` braucht dann keine zweite
 *    Rechnung.
 *
 * Rueckgabe `0` heisst „es passt keine Spur" — die Vorgabe fuer eine
 * ungemessene Breite (jsdom hat kein Layout) und fuer eine Breite unter der
 * Mindestbreite. Beide Faelle sind von „eine Spur passt" (Rueckgabe `1`) zu
 * unterscheiden.
 */
export function passendeSpuren(breite, mindestbreite, abstand = 0) {
  if (!Number.isFinite(breite) || !Number.isFinite(mindestbreite) || mindestbreite <= 0) return 0;
  return Math.max(0, Math.floor((breite + abstand) / (mindestbreite + abstand)));
}

/**
 * Die AUSGEGLICHENE Spaltenzahl fuer `kacheln` Kacheln bei `spuren` Spuren, die
 * in die Breite passen.
 *
 * `ceil(n / ceil(n / k))` — die KLEINSTE Spaltenzahl, die mit der KLEINSTEN
 * Zeilenzahl auskommt: `ceil(n / k)` ist die geringste Zahl von Zeilen, die `k`
 * Spuren ueberhaupt erlauben, und `ceil(n / zeilen)` die kleinste Spaltenzahl,
 * die sie erreicht. (Beides gilt fuer jedes `n ≥ 1`, `k ≥ 1` — gemessen.)
 *
 * Der Unterschied zu `auto-fit` ist die VERTEILUNG, nicht die Zeilenzahl:
 * `auto-fit` fuellt die erste Zeile maximal und legt den Rest in die letzte —
 * bei sieben Kacheln und sechs passenden Spuren steht dort 6 + 1 (die letzte
 * Kachel als Banner ueber die ganze Zeile — der Fotobefund am Lieferantendetail),
 * hier 4 + 3.
 *
 * 🔴 **Die Zusage „die Zeilen unterscheiden sich um hoechstens EINE Kachel" gilt
 *    NICHT fuer jedes Zahlenpaar — und die Grenze ist gemessen, nicht
 *    geschaetzt.** Mit `zeilen = ceil(n / k)` und `rest = n mod zeilen` ist der
 *    Abstand zwischen der vollsten und der letzten Zeile `zeilen − rest`
 *    (0 bei `rest = 0`); er ist also genau dann ≤ 1, wenn `rest` 0 oder
 *    `zeilen − 1` ist. Gegenbeispiele: `n = 10, k = 4` → 4 + 4 + 2;
 *    `n = 13, k = 5` → 5 + 5 + 3. Ein kleineres `c` waere dort ausgeglichener,
 *    braeuchte aber MEHR Zeilen — der Tausch ist eine eigene Entscheidung und
 *    hier bewusst nicht getroffen.
 *
 *    🔴 **Fuer den Bestand traegt sie trotzdem, auch das gemessen:** keine
 *    `KpiRow` im Repo fuehrt mehr als sieben Kacheln (die groesste ist der
 *    Lieferantenstreifen, `VendorDetails.jsx`). Sie gilt deshalb fuer `k ≥ 4`;
 *    `k = 3` verletzt sie (`7 → 3 + 3 + 1`) und ist DATENabhaengig erreichbar —
 *    die Kachelzahl steht nicht in der Breite, sondern in den Daten (ein
 *    Lieferant mit Returns UND Service Cases fuehrt immer sieben). Wie zuvor
 *    `auto-fit` liefert das dort `3 + 3 + 1`, also keine Regression. Die Probe
 *    `KpiRow.test.jsx` fuehrt die Zusage ueber `n ≤ 7`, `k ≥ 4` und das
 *    Gegenbeispiel daneben, damit die Grenze nicht als „gilt immer" gelesen wird.
 *
 * `k < 1` → `1`: passt keine Spur in die Breite, bleibt EINE Spalte. Die
 * Rueckgabe ist nie `0` — `repeat(0, …)` ist ungueltiges CSS.
 */
export function spaltenzahl(kacheln, spuren) {
  const n = Number.isFinite(kacheln) ? Math.floor(kacheln) : 0;
  if (n < 1) return 1;
  const k = Number.isFinite(spuren) ? Math.floor(spuren) : 0;
  if (k < 1) return 1;
  return Math.max(1, Math.ceil(n / Math.ceil(n / k)));
}

/**
 * KpiRow — eine Reihe gleich breiter KPI-Kacheln mit gestrichelten Trennern.
 *
 * @description
 *   Eine `Paper`-Flaeche, die N Kinder in gleich breite Spuren legt und
 *   zwischen ihnen eine gestrichelte Linie zieht. Die Spurenzahl kommt ab `sm`
 *   aus der GEMESSENEN Breite (`ResizeObserver`), unter `xs` sind es genau
 *   zwei; die Zeilen brechen ausgeglichen um (`ceil(n / ceil(n / k))`). Der
 *   Baustein ist die Hausform fuer Kopfzeilen-Kennzahlen — nicht eine Karte,
 *   nicht ein Tabellenkopf.
 *
 * @useWhen
 *   - Eine Zeile von Kennzahlen unter einem Seitenkopf oder einer Karte
 *   - Kacheln, die gleich breit sein sollen und sich die Trennlinien teilen
 *   - Eine Kachel, die unter `xs` die volle Zeile braucht (`span`, s. `@props`)
 *
 * @dontUseFor
 *   - Eine einzelne Kennzahl — dafuer `StatTile` allein (die Reihe bringt
 *     Rahmen, Radius und Trenner mit und rahmt dann leer)
 *   - Zwei GETRENNTE Reihen, die gleich breite Spalten haben sollen — das kann
 *     dieses Raster nicht (die Spurenzahl haengt an der Kachelzahl je Reihe);
 *     dafuer fuehrt man EINE Reihe
 *   - Ein Raster aus Karten mit eigenem Inhalt — dafuer `Grid`/`Stack`
 *
 * @related
 *   - `StatTile` — der uebliche Inhalt einer Kachel
 *   - `KpiCell` — der Marker um ein Kind (ein Fragment, s. `@props`)
 *   - `Panel` — die Flaeche fuer alles, was keine Kennzahlenreihe ist
 *
 * @reuseHints
 *   - Weitere Detailseiten-Koepfe auf dieselbe Reihe ziehen
 *   - Sobald eine Kachel ueber zwei Spuren muss: `span` statt eigenem `sx`
 *
 * @props
 *   - children — die Kacheln. Jedes Kind wird in ein eigenes `Box` gehuellt
 *   - child.span — `gridColumn`-Wert fuer DIESES Kind (z. B.
 *     `{ xs: '1 / -1', sm: 'auto' }`). 🔴 Gelesen wird er HIER, nicht in
 *     `KpiCell`: das ist ein Fragment und reicht keine Props durch — seine
 *     Props sind fuer React nur Daten. Nur `gridColumn` wird gesetzt
 *   - maxMobile: number (default 4) — 🔴 F5-9 (UI-Durchgang 2 Teil 5,
 *     2026-10-09): unter `sm` zeigt die Reihe HOECHSTENS so viele Kacheln; der
 *     Rest steht hinter „All metrics". Keine Zahl geht verloren, nur die
 *     Reihenfolge aendert sich. Der Deckel greift nur mobil (`{ xs: 'none',
 *     sm: 'flex' }`) — der Desktop sieht unveraendert alle Kacheln, und der
 *     Aufklapp-Knopf ist dort `display: none`. `maxMobile={Infinity}` schaltet
 *     ihn ab. Der Grund fuer die Zahl steht in §5 F-2 des Teil-5-Plans:
 *     fuenf Kacheln sind mobil drei Zeilen (~540 px) und nehmen den halben
 *     ersten Bildschirm; vier sind zwei Zeilen.
 *
 *     🔴 WANN DER AUFRUFER `maxMobile={Infinity}` SETZT (Nachbesserung 2,
 *     2026-10-09, Entscheid des Orchestrators zu R16 W1/W2 + R15 W5): die
 *     Vorgabe 4 bleibt — aber an jeder Flaeche mit mehr als vier Kacheln,
 *     deren ab der FUENFTEN Kachel verborgene Kacheln eine HANDLUNG (`onClick`/Link)
 *     oder eine STATUSFARBE (`valueColor`/`color`) tragen, hebt der Aufrufer
 *     den Deckel auf. Ein Klickziel oder ein Farbsignal darf nicht hinter einem
 *     Knopf liegen. Die Desktop-Reihenfolge bleibt unangetastet — `maxMobile`
 *     wirkt nur unter `sm`.
 *     Die Flaechenliste (Flaeche · Kacheln · Deckel · Grund) steht in
 *     `docs/knowledge/design-language/pattern-library.md` §KPI.
 *   - sx — zuletzt angewandt (der Aufrufer gewinnt)
 *   - …rest — an das `Paper` durchgereicht
 *
 * ── Bauart und Begruendungen (F8 / R15 W1 / R16 W2 / E3 Folgerunde 13) ──
 *
 * - One Paper that holds N "cells" side by side with dotted dividers between them.
 * - Divider fix: every cell gets a borderLeft, but the inner Box is shifted -1px left
 *   so the first cell's borderLeft (and the first cell of every wrapped row) is always
 *   clipped by the Paper's `overflow: hidden`. No more orphan border on row-start cells.
 * - 🔴 E-W7 (2026-10-06): jede Zelle richtet ihren Inhalt OBEN aus
 *   (`justifyContent: 'flex-start'`, vorher `'center'`). In einer gestreckten Zelle
 *   zentriert eine 2-zeilige Kachel auf anderer Hoehe als eine 3-zeilige — der Wert
 *   springt zwischen Nachbarn. `StatTile.reserveHint` ist der Vertrag dagegen, gilt
 *   aber PRO KACHEL, eine Reihe darf mischen. Eine feste Zeilenhoehe fuer den WERT
 *   behebt es nicht — sie verschiebt beide gleich.
 * - 🔴 F8 (UI-Durchgang 2, 2026-10-07): **gleich breite Spalten statt `flex: 1 1
 *   160px`.** Mit `flex-wrap` wuchs die allein umbrechende Kachel einer Reihe auf
 *   die RESTBREITE der Zeile (Vendor-Detail, Foto: „Service Cases allein in
 *   Zeile 2"). Das Raster gibt jeder Kachel einer Reihe dieselbe Breite.
 *
 *   ⚠️ **`auto-fit`, nicht `auto-fill`** — und der Unterschied ist der ganze
 *   Punkt: `auto-fit` schliesst Spuren, die in JEDER Zeile leer bleiben. Drei
 *   Kacheln fuellen deshalb weiter die volle Breite (drei breite Spuren statt
 *   drei schmaler + leerem Rest). Es schliesst sie NICHT, solange in jeder Spur
 *   eine Kachel steht — eine allein umbrechende Kachel bleibt darum
 *   SPALTENBREIT statt zeilenbreit.
 *
 *   🔴 **Nachtrag 2026-10-08 (R15 W1): das Telefonband bekommt GENAU ZWEI
 *   Spuren — `{ xs: …, sm: … }`, nicht eine Regel fuer alle Breiten.** Das
 *   reine `auto-fit` haette unter `sm` `floor(Inhalt / 160)` Spuren gegeben: ab
 *   **504 px** Viewport (Inhalt 480 px nach `PAGE_GUTTER.xs`) waeren das DREI
 *   geworden — ein Wechsel 2 → 3 mitten im Telefonband, den niemand entworfen
 *   hat und den die alte Regel (`xs: calc(50% - 1px)`) nie hatte. Unter `sm`
 *   gilt deshalb die alte Zweispaltigkeit, ab `sm` faellt die Spurenzahl
 *   weiter aus der Breite. Die zwei Werte sind bewusst VERSCHIEDENE Formen:
 *   `repeat(2, minmax(0, 1fr))` (gleich, schrumpfbar) gegen
 *   `repeat(auto-fit, minmax(160px, 1fr))` (so viele wie passen).
 *
 *   🔴 **Nachtrag 2026-10-08 (R16 W2): die Paritaet zum alten `flex` gilt nur
 *   fuer GERADE Kachelzahlen — die neue Regel
 *   `& > :last-of-type:nth-of-type(odd)` stellt sie fuer ungerade her.** Der Satz
 *   „unter `sm` … wie das alte `xs: calc(50% - 1px)`" war die halbe Wahrheit.
 *   Die alte Regel war `flex: 1 1 calc(50% - 1px)` in einem `flex-wrap`-
 *   Behaelter, und `flex-grow: 1` zog eine ALLEIN umbrechende Kachel auf die
 *   VOLLE Zeilenbreite. Bei 1, 3, 5 oder 7 Kacheln endete die Reihe damit auf
 *   einer vollen Kachel, bei 2, 4 oder 6 auf einer halben. Das feste
 *   `repeat(2, …)` gab dagegen JEDER Kachel eine halbe Spur — die ungerade
 *   Kachelzahl war genau die Stelle, an der die neue Form von der alten abwich
 *   (R16 W2, gemessen am Telefonbild).
 *
 *   Die Regel haengt an der KINDERZAHL und steht deshalb am Raster, nicht an
 *   der Zelle: `:last-of-type:nth-of-type(odd)` trifft das letzte Kind genau dann,
 *   wenn seine Position ungerade ist (1., 3., 5., 7. …). Sie gilt NUR unter
 *   `xs`; ab `sm` steht `auto`, und die Spur kommt wieder aus dem Raster.
 *
 *   🔴 **Nachtrag 2026-10-08 (E3 Folgerunde 10): `:last-of-type:nth-of-type(odd)`
 *   statt `:last-child:nth-child(odd)` — und warum das hier DASSELBE trifft.**
 *   `@emotion/cache` traegt in der Entwicklung einen Alarm
 *   (`createUnsafeSelectorsAlarm`, `stylis-plugins.ts:166-173`), dessen Muster
 *   `/(:first|:nth|:nth-last)-child/` lautet; er ruft auf jedem Treffer
 *   `console.error` („potentially unsafe when doing server-side rendering").
 *   `:nth-child` fiel darunter, und weil jede Seite mit einer `KpiRow` die Regel
 *   einfuegt, machte die Warnung drei Rauchproben rot
 *   (`tests/fulfillment/confirm-dialog-radius.spec.ts` D.1,
 *   `tests/fulfillment/tabellen.spec.ts` F.1,
 *   `tests/incoming-inventory/tabelle.spec.ts` I.1) — auf `master` gruen, nur
 *   auf diesem Zweig rot. `:nth-of-type` steht NICHT in jenem Muster.
 *
 *   ⚠️ **Die beiden Selektoren sind hier gleich, weil ALLE direkten Kinder des
 *   Rasters denselben Tag tragen — und das ist keine Annahme ueber die Aufrufer,
 *   sondern Bauart dieser Datei:** `React.Children.map` unten huellt JEDES Kind
 *   in ein eigenes `<Box>` (MUI-Vorgabe `div`) — ein Fragment dabei als GANZES,
 *   nicht seine Teile. Ein Aufrufer kann dem Raster also gar kein andersartiges
 *   Element unterschieben. Genau deshalb ist `:last-of-type` = `:last-child`
 *   und `:nth-of-type(odd)` = `:nth-child(odd)`.
 *   `KpiRow.test.jsx` haelt diese Bauart mit einer eigenen Probe fest (gemischte
 *   Kinder, ein TagName) — faellt sie, ist auch diese Gleichheit gefallen.
 *   ⚠️ Der vom Alarm vorgeschlagene Ersatz ist NICHT ueberall richtig: in
 *   `ViewHeader.jsx:990` waere `:first-of-type` falsch, weil Titel und Adornment
 *   denselben Tag tragen koennen. Hier ist der Fall umgekehrt — EIN Tag fuer
 *   alle —, und nur darum ist der Tausch zulaessig.
 *
 *   ⚠️ **Die Trennlinie traegt sie nicht falsch.** Eine volle Kachel beginnt in
 *   Spalte 1, und die erste Spalte liegt wegen `ml: '-1px'` genau 1 px links vom
 *   Papier-Innenraum — ihre `borderLeft` wird vom `overflow: hidden` des `Paper`
 *   beschnitten, wie bei jeder anderen zeilenersten Kachel. Kein zweiter Weg,
 *   keine Sonderregel.
 *
 *   Belegte Stellen: `components/TaxonomyQualityWidget.jsx:161` (EINE Kachel,
 *   „Issues" mit Chips — die haeufigste Form des Falls), `IncomingInventory.jsx:829`
 *   (sechs) und die Market-Lab-Karten (fuenf).
 *
 *   🔴 **Nachtrag 2026-10-08 (E3 Folgerunde 13): die Zeilen brechen
 *   AUSGEGLICHEN um — `ceil(n / ceil(n / k))` statt `auto-fit`.** Hier stand
 *   bis dahin als erste Grenze „Es balanciert die Zeilen nicht. Sieben Kacheln
 *   brechen bei sechs passenden Spuren weiter 6+1". Genau das war der
 *   Fotobefund, den F8 als Belegstelle fuehrt: Lieferantendetail bei 1440 px,
 *   sieben Kacheln, sechs passen — sechs in Zeile 1 und „Service Cases" allein
 *   in Zeile 2. `auto-fit` **fuellt die erste Zeile maximal** und gibt den Rest
 *   in die letzte; gleiche Breiten allein machen daraus ein gleich breites
 *   Banner, keine ausgeglichene Reihe.
 *
 *   Die Spaltenzahl kommt jetzt aus der GEMESSENEN Breite des Rasters
 *   (`ResizeObserver` am Rasterbehaelter) und der Mindestbreite, die in die
 *   Breite passt (`k`); die zwei reinen Funktionen stehen oben
 *   (`passendeSpuren`, `spaltenzahl`). Ergebnis: die Zeilen unterscheiden sich
 *   um **hoechstens eine** Kachel — 4 + 3 statt 6 + 1; passt alles in eine
 *   Zeile, bleibt es eine Zeile (`n ≤ k` → `c = n`).
 *
 *   ⚠️ **Vor der ersten Messung steht das alte `auto-fit`** — nicht eine
 *   Spalte. Ein Rueckfall auf `1` liesse die Reihe aufblitzen und dann
 *   umspringen; `auto-fit` ist der Zustand, den der erste Anstrich ohnehin
 *   zeigt, und die Messung kommt im selben Bild.
 *
 *   ⚠️ **Was das Raster NICHT tut — eine Grenze, benannt:** es **gleicht zwei
 *   GETRENNTE Reihen nicht an.** Fuehrt eine Seite zwei `KpiRow` (z. B.
 *   `IncomingInventory.jsx` mit 4 und 3 Kacheln), belegen sie verschieden viele
 *   Spuren und haben damit verschiedene Spaltenbreiten. Wer gleiche Breiten
 *   ueber zwei Reihen will, fuehrt EINE Reihe — das ist eine Aenderung an der
 *   Seite, nicht an diesem Baustein.
 *
 * Usage:
 *   <KpiRow>
 *     <KpiCell>...</KpiCell>
 *     <KpiCell span={{ xs: '1 / -1', sm: 'auto' }}>...</KpiCell>
 *   </KpiRow>
 */
export default function KpiRow({ children, sx, maxMobile = 4, ...rest }) {
  const theme = useTheme();

  // 🔴 EINE Quelle fuer die Mindestbreite. Derselbe Wert speist die MESSUNG
  //    (`passendeSpuren` im Beobachter) und die RUECKFALLREGEL im CSS; zwei
  //    getrennte Zahlen liefen auseinander, sobald jemand eine davon anfasst.
  //    `theme.spacing(20)` ist die Zahl, die hier seit F8 steht (160 px bei der
  //    Vorgabe-Spacing von 8) — dieselbe wie das frueher hier stehende
  //    `flex-basis: 160px`.
  const mindestbreite = React.useMemo(() => {
    const wert = parseFloat(theme.spacing(20));
    return Number.isFinite(wert) && wert > 0 ? wert : 160;
  }, [theme]);

  const rasterRef = React.useRef(null);
  // 🔴 G0b.5 (2026-10-13) — die Beobachter-Logik stand hier INLINE, und sie war
  // die SCHWÄCHERE Fassung derselben Mechanik, die `useContainerWidth` (BW4,
  // G0b.3) verallgemeinert: derselbe Wächter (`!el || typeof ResizeObserver ===
  // 'undefined'`), dieselbe letzte-Eintrag-Lesung, dieselbe 0-Verwerfung — aber
  // **ohne** `requestAnimationFrame`-Drosselung und **ohne** Schleifenschutz
  // (Plan §3.3 „Neu aufgenommen", Nachbesserung 2; `useContainerWidth.js`
  // @reuseHints). Zwei Kopien derselben Mechanik im Baum, und die schwächere
  // gehörte zu der Fläche, die der neue Hook als Vorbild zitiert. Jetzt EINE
  // Quelle: der Hook liefert `{ width }`, und das passt **eins zu eins** in die
  // `k`-Berechnung darunter.
  //
  // 🔴 `width === null` heißt unverändert NOCH NICHT GEMESSEN — nicht „null
  // breit". Die zwei Lagen sind verschieden: eine gemessene Breite von 0 gäbe
  // `k = 0` und damit eine Spalte, eine ungemessene gibt den `auto-fit`-Rückfall.
  // Eine gemeldete 0 verwirft der Hook selbst (`useContainerWidth.js:188-189` —
  // „nicht ausgelegt", nicht „null breit"). Unter jsdom bleibt es IMMER bei
  // `null` (der `ResizeObserver`-Stummel in `setupTests.js` ruft seinen Rückruf
  // nie) — der ausgeglichene Zweig ist dort unerreichbar, außer eine Probe setzt
  // einen feuernden Beobachter ein (siehe `KpiRow.test.jsx`).
  const { width: breite } = useContainerWidth(rasterRef);

  // 🔴 ZWEI ZAHLEN — sie beantworten zwei verschiedene Fragen. Bis zur
  //    Nachbesserung 2 (R15-Blocker 1 / R16 W3, 2026-10-09) speiste EINE Zahl
  //    beide, und auf `/vendors/:id` stand „All metrics (7)" über fünf
  //    Kennzahlen.
  //
  //      `anzahl`  = PLÄTZE. `React.Children.count` ist dokumentiert als
  //                  „equal to the number of times that a callback passed to
  //                  `map` or `forEach` would be invoked", und gerendert wird
  //                  unten genau mit `React.Children.map`. Sie speist die
  //                  SPALTENRECHNUNG — das Raster verteilt Plätze, nicht
  //                  Kacheln: `{cond && <KpiCell/>}` mit falschem `cond` wird
  //                  von `Children.map` AUFGERUFEN und liefert einen (leeren)
  //                  Kasten. Wer hier `toArray` benutzte, verteilte Kacheln auf
  //                  Spuren, die es nicht gibt, und die letzte Zeile trüge eine
  //                  einzelne Kachel — genau der Fotobefund, den F8/E3 behebt.
  //
  //      `kacheln` = GEFÜLLTE KACHELN. `React.Children.toArray` lässt genau die
  //                  `null`/`false`-Kinder WEG, die oben einen leeren Kasten
  //                  ergeben. Sie speist das ETIKETT („All metrics (n)") und
  //                  die DECKEL-ENTSCHEIDUNG — dort ist die Platzzahl schlicht
  //                  falsch: `VendorDetails.jsx` führt fünf feste plus zwei
  //                  bedingte `KpiCell`, und ohne `rma_config` meldete die
  //                  Reihe sieben Kennzahlen, von denen zwei nicht existieren.
  //
  //    🔴 Warum das den Deckel nicht aushöhlt: ist `kacheln > maxMobile`, so
  //    liegt mindestens eine GEFÜLLTE Kachel auf einem Platz ab `maxMobile` —
  //    unterhalb liegen nur `maxMobile` Plätze, es können dort also höchstens
  //    `maxMobile` gefüllte stehen. Der Knopf deckt damit IMMER mindestens eine
  //    echte Kachel auf; er kann nie ins Leere aufklappen.
  const anzahl = React.Children.count(children);
  const kacheln = React.Children.toArray(children).length;

  // 🔴 F5-9 (UI-Durchgang 2 Teil 5, 2026-10-09): mobil hoechstens `maxMobile`
  //    Kacheln, der Rest hinter „All metrics". Keine Zahl geht verloren — nur
  //    die Reihenfolge aendert sich (§5 F-2, entschieden).
  //
  //    🔴 Warum der Deckel als CSS-Regel und nicht als `useMediaQuery`: die
  //    Kappung ist eine LAYOUT-Frage, und `display: none` haelt die verborgenen
  //    Kacheln gleichzeitig aus dem Baum der Hilfsmittel. Ein Zustandswechsel
  //    ueber `matchMedia` haette im ersten Bild die falsche Zahl gezeigt und
  //    die Reihe umspringen lassen — dieselbe Ueberlegung wie beim Rueckfall
  //    `auto-fit` darunter.
  //
  //    ⚠️ Der Deckel greift NUR mobil. `maxMobile` ist eine OBERgrenze, keine
  //    Vorgabe: Reihen mit vier oder weniger Kacheln (der Bestand) sehen
  //    unveraendert aus, und der Aufklapp-Knopf entsteht gar nicht erst.
  const aufklappbar = Number.isFinite(maxMobile) && kacheln > maxMobile;
  const [alleZeigen, setAlleZeigen] = React.useState(false);
  const gekappt = aufklappbar && !alleZeigen;
  const rasterId = React.useId();

  // `null` = ungemessen → `auto-fit` (der Rückfall, s. Docstring oben).
  const spuren = breite == null ? 0 : passendeSpuren(breite, mindestbreite);
  const spaltenRegel = breite == null
    ? `repeat(auto-fit, minmax(${mindestbreite}px, 1fr))`
    : `repeat(${spaltenzahl(anzahl, spuren)}, minmax(0, 1fr))`;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: PANEL_RADIUS,
        overflow: 'hidden',            // clips the -1px-shifted borderLeft on row-first cells
        bgcolor: 'background.paper',
        boxShadow: SHADOWS.panel,
        border: '1px solid',
        borderColor: 'rgba(148,163,184,0.18)',
        ...sx,
      }}
      {...rest}
    >
      {/*
       * ml: '-1px' shifts the grid 1px to the left.
       * Every cell has borderLeft. The first cell's border sits at -1px → clipped.
       * A cell at the start of a wrapped row is in grid COLUMN 1, i.e. also at
       * -1px → also clipped.
       * Result: dividers only appear *between* cells, never on the first cell of any row.
       */}
      <Box
        ref={rasterRef}
        // 🔴 F5-9: die Kennung bindet den Aufklapp-Knopf an DAS Raster, das er
        //    aufklappt (`aria-controls`). `useId` statt einer festen Zeichen-
        //    kette: eine Seite fuehrt mehrere Reihen (`IncomingInventory` zwei),
        //    und zwei gleiche Kennungen waeren ungueltiges HTML.
        id={rasterId}
        sx={{
          display: 'grid',
          // ZWEI Regeln, und die Aufteilung ist eine Entscheidung (R15 W1,
          // 2026-10-08):
          //   · unter `sm` GENAU zwei Spuren — wie das alte `xs: calc(50% - 1px)`
          //     und wie `pattern-library.md` §Responsive es zusagt. Das reine
          //     `auto-fit` haette hier `floor(Inhalt / 160)` Spuren gegeben: ab
          //     504 px Viewport waeren das DREI geworden (Wechsel 2 → 3 mitten
          //     im Telefonband).
          //     ⚠️ Die Paritaet gilt fuer GERADE Kachelzahlen; bei ungerader
          //     stellt sie die Regel `:last-of-type:nth-of-type(odd)` unten her
          //     (R16 W2, 2026-10-08 — die alte `flex-grow: 1` zog die allein
          //     umbrechende Kachel auf die volle Zeile).
          //   · ab `sm` faellt die Spaltenzahl aus der GEMESSENEN Breite (E3
          //     Folgerunde 13, 2026-10-08): `spaltenRegel` ist
          //     `repeat(c, minmax(0, 1fr))` mit `c = spaltenzahl(n, k)` — die
          //     Zeilen unterscheiden sich dann um hoechstens EINE Kachel. Vor
          //     der ersten Messung steht hier `auto-fit`, NICHT eine Spalte
          //     (Begruendung im Docstring oben).
          //     `minmax(<mindestbreite>px, 1fr)` ist dieselbe Zahl wie das
          //     frueher hier stehende `flex-basis: 160px` (F8).
          // 🔴 Was davon in Jest pruefbar ist, und was nicht: MUI legt
          //     Breakpoint-Werte in `@media`-Bloecke, und jsdom wertet keine
          //     Media-Queries aus — `getComputedStyle` liefert dann `''`
          //     (gemessen, `Panel.test.jsx` §„pad sm und md"). BEIDE Werte
          //     liest `KpiRow.test.jsx` deshalb aus den AUSGELIEFERTEN Regeln
          //     (CSSOM); die Hausform dafuer steht in
          //     `common/SplitButton.test.jsx` (`ausgelieferteRegeln`).
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: spaltenRegel,
          },
          // 🔴 R16 W2 (2026-10-08): eine ALLEIN stehende Kachel spannt unter
          // `xs` die VOLLE Zeile — die Paritaet zum alten `flex-grow: 1`
          // (Begruendung und Grenzen im Docstring oben).
          // `:last-of-type:nth-of-type(odd)` trifft das letzte Kind nur bei
          // ungerader Position (1., 3., 5., 7. …), also genau dann, wenn es
          // allein in seiner Zeile steht; bei gerader Kachelzahl bleibt es bei
          // der halben Spur. Ab `sm` steht `auto` — die Spur kommt dort wieder
          // aus dem Raster; die ausgeglichene Spaltenzahl (E3 Folgerunde 13)
          // laesst im Bereich des Bestands (`n ≤ 7`, `k ≥ 4`) keine Zeile mehr
          // mit einer einzelnen Kachel stehen, die Regel ist dort also nicht nur
          // aus, sondern gegenstandslos. (Die Grenze der Zusage steht bei
          // `spaltenzahl` oben — sie gilt nicht fuer jedes Zahlenpaar.)
          // ⚠️ `-of-type` statt `-child` wegen des Emotion-Alarms (Docstring
          // oben); hier gleichwertig, weil ALLE Kinder dieses Rasters die `Box`
          // aus `React.Children.map` sind — ein Tag.
          // ⚠️ Emotion: `&` MUSS am Anfang stehen, sonst gilt der Schluessel als
          // Stil-Eigenschaft und nicht als Selektor.
          '& > :last-of-type:nth-of-type(odd)': {
            gridColumn: { xs: '1 / -1', sm: 'auto' },
          },
          alignItems: 'stretch',
          ml: '-1px',
        }}
      >
        {React.Children.map(children, (child, i) => {
          // 🔴 F14 (UI-Durchgang 2, Teil 3, 2026-10-08): ein Kind darf seine
          //    Spanne SELBST angeben (`<KpiCell span={…}>`). Gelesen wird sie
          //    hier, nicht in `KpiCell` — das ist ein Fragment und reicht keine
          //    Props durch; seine Props sind fuer React nur Daten. Der Wert ist
          //    ein `gridColumn`-Wert und wird unveraendert gesetzt (z. B.
          //    `{ xs: '1 / -1', sm: 'auto' }`).
          //
          //    ⚠️ `child?.props?.span`, nicht `child.props.span`: `Children.map`
          //    ruft den Rueckruf AUCH fuer `null`-Kinder auf (`{cond && <…>}`
          //    mit falschem `cond`) — genau deshalb ist `Children.count` die
          //    Zahl der PLÄTZE (s. u.), nicht der Kacheln — blanker Zugriff wuerfe.
          //
          //    ⚠️ Gegen die Regel `& > :last-of-type:nth-of-type(odd)` (unten)
          //    verliert `gridColumn` aus dem `sx` den Kaskadenlauf: sie traegt
          //    eine Klasse mehr. Sie trifft aber nur das LETZTE Kind bei
          //    UNGERADER Position; wer genau dort eine andere Spanne will, muss
          //    sie in jener Regel unterbringen, nicht hier.
          const spanne = child?.props?.span;
          // 🔴 F5-9: jenseits von `maxMobile` nur mobil verborgen — `display:
          //    none` nimmt die Kachel aus dem Baum der Hilfsmittel UND aus dem
          //    Layout; ab `sm` steht sie wieder als `flex` (die Kachelform, die
          //    sie vorher trug). Der Desktop sieht damit unveraendert alle.
          const verborgen = gekappt && i >= maxMobile;
          return (
            <Box
              sx={{
                ...(spanne ? { gridColumn: spanne } : null),
                minWidth: 0,
                // 🔴 R15 „Nicht gemessen" (Nachbesserung 2, 2026-10-09): hier
                //    standen rohe Zahlen (1.5 / 2). `check_ui_tokens.py` sieht
                //    die Kurzformen `px`/`py`/`pb` NICHT (sein Muster greift nur
                //    `padding|margin|gap…`), sein grüner Lauf war für diese
                //    Zeilen also keine Entlastung. Jetzt Tokens — dieselben
                //    Werte, SP.sm = 1.5 und SP.card = 2.
                px: { xs: SP.sm, sm: SP.card },
                py: { xs: SP.sm, sm: SP.card },
                display: verborgen ? { xs: 'none', sm: 'flex' } : 'flex',
                flexDirection: 'column',
                // 🔴 E-W7: `flex-start`, NICHT `center` — in einer gestreckten
                // Zelle zentriert eine 2-zeilige Kachel auf einer anderen Höhe
                // als eine 3-zeilige, der Wert springt zwischen Nachbarn.
                // `reserveHint` ist PRO KACHEL erklärt, eine Reihe darf mischen.
                justifyContent: 'flex-start',
                borderLeft: '1px dashed',
                borderColor: 'divider',
                transition: 'background-color 0.15s',
                ...hoverOnly({ bgcolor: 'action.hover' }),
              }}
            >
              {child}
            </Box>
          );
        })}
      </Box>
      {/* 🔴 F5-9: der Aufklapp-Knopf. Nur mobil sichtbar (`sm: none`) und nur,
          wenn es ueberhaupt etwas aufzuklappen gibt — der Bestand mit vier
          oder weniger Kacheln sieht ihn nie. `aria-expanded` meldet den
          Zustand, `aria-controls` zeigt auf das Raster. */}
      {aufklappbar && (
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'flex-end', px: SP.sm, pb: SP.tight }}>
          <Button
            size="small"
            color="inherit"
            aria-expanded={alleZeigen}
            aria-controls={rasterId}
            onClick={() => setAlleZeigen((v) => !v)}
            sx={{ color: 'text.secondary' }}
          >
            {alleZeigen ? 'Show fewer' : `All metrics (${kacheln})`}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

/**
 * KpiCell — der Marker um EIN Kind der Reihe („optional semantic alias").
 *
 * Ein Fragment: es rendert nichts Eigenes, `KpiRow` huellt jedes Kind selbst in
 * ein `Box`.
 *
 * 🔴 Es reicht KEINE Props durch — ein Fragment kann das nicht, und `sx` hier
 * war darum bis 2026-10-07 wirkungslos (am Aufrufer nachgemessen). Wer eine
 * Kachel ueber zwei Spuren braucht, schreibt die Angabe trotzdem HIER:
 * `KpiRow` liest `child.props.span` vom ELEMENT, nicht von dieser Funktion
 * (F14, `@props` am Baustein oben).
 */
export const KpiCell = ({ children }) => <>{children}</>;
